import { useState } from 'react';

import {
  Box,
  Button,
  Card,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';

import StatusBar from '@/designs-dev/device/StatusBar';
import TabBar from '@/designs-dev/device/TabBar';
import BottomSheet from '@/designs-dev/device/BottomSheet';
import AccountBar, { type Account } from '@/karehero-app/access/AccountBar';
import AppBar from '@/karehero-app/shell/AppBar';
import BackChevron from '@/karehero-app/shell/BackChevron';
import ScreenRoot from '@/components/wrapped/ScreenRoot';
import { DocActionsSheet, EditDocSheet } from '@/karehero-app/care/DocSheets';
import ActionDetail from '@/karehero-app/actions/ActionDetail';
import { type Item } from '@/components/wrapped/ActionCard';
import CallSummary from '@/karehero-app/care/CallSummary';
import { CALLS } from '@/karehero-app/care/fixtures';
import PushScreen from '@/designs-dev/device/PushScreen';
import UploadDocumentButton from '@/karehero-app/shell/UploadDocumentButton';
import ClaimRow from '@/karehero-app/tell-us-once/intake/ClaimRow';
import ClaimSheet from '@/karehero-app/tell-us-once/benefits/ClaimSheet';
import {
  type Claim,
  type ResultContent,
} from '@/karehero-app/tell-us-once/benefits/ResultScreen';
import FactList from '@/karehero-app/tell-us-once/intake/FactList';
import {
  ASSESS_ROWS,
  CARE_PROFILE,
  PROFILE_CALLS,
  PROFILE_DOCS,
  PROFILE_TABS,
  type ProfileTabId,
} from '@/karehero-app/tell-us-once/intake/content';

const SCREEN_PX = 2;

// The profile's one action, in both its states. Defined once because it moves:
// pinned while it is asking them to confirm, in the page once they have.
//
// Coral because it invites rather than concludes: the theme's opt-in coral
// class, the same coral as every other coral button in Loft.
const ProfileAction = ({
  confirmed,
  onClick,
}: {
  confirmed: boolean;
  onClick: () => void;
}) => (
  <Button
    className='coral'
    variant='contained'
    size='large'
    fullWidth
    onClick={onClick}
  >
    {confirmed ? CARE_PROFILE.primary : CARE_PROFILE.review.confirm}
  </Button>
);

const LineRow = ({
  title,
  subtitle,
  onClick,
}: {
  title: string;
  subtitle: string;
  onClick?: () => void;
}) => (
  <Card
    sx={{ p: 2, cursor: onClick ? 'pointer' : 'default' }}
    onClick={onClick}
  >
    <Stack
      sx={{ alignItems: 'center', justifyContent: 'space-between' }}
      direction='row'
      spacing={1.5}
    >
      <Stack sx={{ flex: 1, minWidth: 0 }} spacing={0.25}>
        <Typography variant='h6'>{title}</Typography>
        <Typography sx={{ color: 'text.secondary' }} variant='body2'>
          {subtitle}
        </Typography>
      </Stack>
    </Stack>
  </Card>
);

/**
 * The profile, in the master prototype's shape: one screen, a segmented control
 * across the top. A copy of that pattern rather than a mount of it, so this flow
 * can carry the segments it needs.
 */
const CareProfileScreen = ({
  initialTab = 'profile',
  reviewed = true,
  initialDoc,
  initialRenaming,
  uploading = false,
  initialCall,
  initialCallAction,
  intro = false,
  editing,
  account,
  results,
  openClaim,
  onBack,
  bare = false,
}: {
  initialTab?: ProfileTabId;
  /**
   * Whether a person has been through the facts and said they read right.
   *
   * Unreviewed, the rows we read out of what they sent still carry their
   * sparkle: nobody has checked them. It is the state the profile is in between
   * the intake filling it and the review confirming it, and the state it goes
   * back to whenever we read something new.
   */
  reviewed?: boolean;
  /**
   * Open with the sheet explaining why they are here.
   *
   * Off by default, and not derived from `reviewed`: a mini on a flows page
   * cannot be clicked, so a sheet that opened itself on every unreviewed profile
   * covered the facts and could never be dismissed. Arriving and having arrived
   * are two states, so they are two frames.
   */
  intro?: boolean;
  /**
   * Harness-only: open one fact for changing, by id. A flows page cannot tap,
   * so the drawer only has somewhere to be seen if a step can start in it.
   */
  editing?: string;
  /** Harness-only: open a document's action sheet, by id. */
  initialDoc?: string;
  /** Harness-only: open a document's rename sheet, by id. */
  initialRenaming?: string;
  /** Harness-only: draw the docs tab with one still arriving. */
  uploading?: boolean;
  /** Harness-only: open a call's summary, by id. */
  initialCall?: number;
  /** Harness-only: open one of that call's actions, by id. */
  initialCallAction?: number;
  /** The app bar. See StepScreen's own `account`. */
  account?: Account;
  /**
   * What the eligibility check found, kept at the top of Assess so it can be
   * come back to. In place of the calculator's own row, which it answers.
   */
  results?: ResultContent;
  /** Harness-only: open with this claim's drawer up. */
  openClaim?: string;
  /**
   * One tab as a page of its own, pushed from somewhere else: a back bar named
   * for the tab, no segmented control and no tab bar. The Care hub concept
   * opens Details, Documents and Calls this way.
   */
  onBack?: () => void;
  /**
   * No status bar or app bar: the caller draws them, so a push can keep the
   * bar still and fade it while this body slides. See PushScreen's `bar`.
   */
  bare?: boolean;
}) => {
  const [tab, setTab] = useState<ProfileTabId>(initialTab);
  const [claim, setClaim] = useState<Claim | undefined>(
    results?.claims.find((c) => c.name === openClaim),
  );
  const [showIntro, setShowIntro] = useState(intro);
  // Confirming happens here, so the button and the rows change together.
  const [confirmed, setConfirmed] = useState(reviewed);
  // The docs tab's own state. Uploading adds a row that arrives before the file
  // does; tapping one opens what can be done to it.
  const [docs, setDocs] = useState(PROFILE_DOCS);
  const [docSheet, setDocSheet] = useState<string | null>(initialDoc ?? null);
  const [renaming, setRenaming] = useState<string | null>(
    initialRenaming ?? null,
  );
  const openDoc = docs.find((d) => d.id === (docSheet ?? renaming));
  const [openCallId, setOpenCallId] = useState<number | null>(
    initialCall ?? null,
  );
  const openCall = CALLS.find((c) => c.id === openCallId);
  // An action opened from a call's summary, and whatever has been ticked off
  // while the summary is up. The actions belong to the call, so they open over
  // it rather than sending you to the Actions tab to find them.
  const [callAction, setCallAction] = useState<Item | null>(
    initialCallAction
      ? (CALLS.flatMap((c) => c.linkedActions).find(
          (a) => a.id === initialCallAction,
        ) ?? null)
      : null,
  );
  const [callActionDone, setCallActionDone] = useState<Record<number, boolean>>(
    {},
  );

  return (
    <ScreenRoot>
      {!bare && <StatusBar />}
      {/* The profile is inside the app, so the avatar's sheet is the settings
          menu proper rather than the two rows the access screens carry. Before
          the spread, so a caller can still say otherwise. */}
      {bare ? null : onBack ? (
        <AppBar
          title={PROFILE_TABS.find((t) => t.id === tab)?.label}
          size='small'
          leading={<BackChevron label='Back' onClick={onBack} />}
        />
      ) : (
        account && <AccountBar inApp {...account} title={CARE_PROFILE.person} />
      )}

      <Box
        sx={{
          px: SCREEN_PX,
          pt: 2,
          flexShrink: 0,
          display: onBack ? 'none' : undefined,
        }}
      >
        <ToggleButtonGroup
          className='segmented'
          exclusive
          fullWidth
          value={tab}
          onChange={(_, v) => v != null && setTab(v)}
        >
          {PROFILE_TABS.map((t) => (
            <ToggleButton key={t.id} value={t.id}>
              {t.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>

      <Box
        sx={{ flex: 1, minHeight: 0, overflow: 'auto', px: SCREEN_PX, pt: 2 }}
      >
        {tab === 'profile' && (
          // Changed in the drawer, with the control the answer takes: a
          // nationality is one of a list, a date of birth is a date, an
          // address is four fields. Inline makes all of them a text box, and
          // the review next door settled on the drawer.
          <FactList
            groups={CARE_PROFILE.groups}
            confirmed={confirmed}
            edit='drawer'
            initialEditing={editing}
          />
        )}

        {tab === 'claims' && results && (
          // The same cards as the results, so what they saw at the end of the
          // check is what they find when they come back to it.
          <Stack spacing={1.5} sx={{ pb: 3 }}>
            <Stack spacing={0.5}>
              <Typography variant='h4'>{results.title}</Typography>
              {results.body && (
                <Typography variant='body1'>{results.body}</Typography>
              )}
            </Stack>
            {results.claims.map((c) => (
              <ClaimRow
                key={c.name}
                icon={c.icon}
                name={c.name}
                description={c.description}
                lead={c.lead}
                onClick={c.about ? () => setClaim(c) : undefined}
              />
            ))}
            {results.claims.length === 0 && results.primary && (
              <Button variant='contained' size='large' fullWidth>
                {results.primary}
              </Button>
            )}
          </Stack>
        )}

        {tab === 'claims' && (
          <Stack spacing={1.5}>
            {ASSESS_ROWS.filter(
              (row) => !(results && row.id === 'calculator'),
            ).map((row) => (
              <ClaimRow
                key={row.id}
                icon={row.icon}
                name={row.name}
                description={row.description}
                // A count where there is work left to do, what the row offers
                // where there is not. The care profile is already filled in.
                count={
                  row.action ??
                  (row.answered !== undefined && row.total !== undefined
                    ? `${row.answered} of ${row.total} answered`
                    : undefined)
                }
                lead={row.lead}
              />
            ))}
          </Stack>
        )}

        {tab === 'docs' && (
          <Stack spacing={1.5}>
            {/* Arriving before the file does, so the list answers the tap
                rather than the upload finishing. */}
            {uploading && (
              <LineRow title='Bank statement.pdf' subtitle='Uploading…' />
            )}
            {docs.map((doc) => (
              <LineRow
                key={doc.id}
                title={doc.name}
                subtitle={doc.when}
                onClick={() => setDocSheet(doc.id)}
              />
            ))}
          </Stack>
        )}

        {tab === 'calls' && (
          <Stack spacing={1.5}>
            {CALLS.map((call) => (
              <LineRow
                key={call.id}
                title={call.title}
                subtitle={`${call.when} · with ${call.withName}`}
                onClick={() => setOpenCallId(call.id)}
              />
            ))}
          </Stack>
        )}

        {/* Only on About, and floating over the list rather than waiting at the
            end of it.

            Only on About because that is the tab it is about: telling us more
            adds to what we hold, and the offer made no sense sitting under a
            list of calls or a claim.

            Floating because the end of this list is a long way down. Sticky
            rather than absolute, so it needs nothing to know the tab bar's
            height, and it settles into the flow when you do reach the bottom. */}
      </Box>

      {/* Pinned under the list in both states, on the page's own ground.
          They cannot say the list reads right without a way to say so wherever
          they have scrolled to, and once they have, Tell us more is the way on
          from a list whose end is a long way down.

          It used to float inside the scroller when confirmed, sticky to the
          bottom, painting background.default over itself to mask the rows
          passing under it. On a Floral White card that mask was a warm beige
          band with two hard edges, which read as a bordered box around the
          button. Out here there is nothing to mask. */}
      {(tab === 'profile' || tab === 'docs') && (
        <Box sx={{ flexShrink: 0, px: SCREEN_PX, py: 1.5 }}>
          {tab === 'profile' ? (
            <ProfileAction
              confirmed={confirmed}
              onClick={confirmed ? () => {} : () => setConfirmed(true)}
            />
          ) : (
            // Floating rather than at the end of the list: a doc list has no
            // end worth scrolling to, and the way to add one should not be
            // somewhere you arrive at.
            <UploadDocumentButton
              onClick={() => {
                // The row lands first and fills in after, so the tap is
                // answered by the list rather than by a spinner over it.
                const id = `upload-${Date.now()}`;
                setDocs((prev) => [
                  {
                    id,
                    name: 'Bank statement.pdf',
                    when: 'Uploading…',
                    read: '',
                  },
                  ...prev,
                ]);
                setTimeout(
                  () =>
                    setDocs((prev) =>
                      prev.map((d) =>
                        d.id === id ? { ...d, when: 'Just now' } : d,
                      ),
                    ),
                  1200,
                );
              }}
            />
          )}
        </Box>
      )}

      {/* The point of landing here is that it is a tab of the app, not the end
          of a flow, so the app's own nav is under it. Inert: the other tabs are
          not part of this work. */}
      {/* What can be done to a document, and renaming it. Both are the Care
          tab's own sheets: a document is the same object wherever it is seen,
          so it should not be managed two different ways. */}
      {docSheet && openDoc && (
        <DocActionsSheet
          onView={() => setDocSheet(null)}
          onEdit={() => {
            setRenaming(docSheet);
            setDocSheet(null);
          }}
          onDelete={() => {
            setDocs((prev) => prev.filter((d) => d.id !== docSheet));
            setDocSheet(null);
          }}
          onClose={() => setDocSheet(null)}
        />
      )}

      {renaming && openDoc && (
        <EditDocSheet
          doc={{ id: 0, name: openDoc.name }}
          onSave={(name) => {
            setDocs((prev) =>
              prev.map((d) => (d.id === renaming ? { ...d, name } : d)),
            );
            setRenaming(null);
          }}
          onClose={() => setRenaming(null)}
        />
      )}

      {/* The call, pushed over the tab. Same screen the Care tab opens: what
          was said on a call, and the actions it left behind, are the same
          thing wherever you reach them from. */}
      {openCall && (
        <PushScreen onClose={() => setOpenCallId(null)}>
          {(close) => (
            <CallSummary
              call={openCall}
              onBack={close}
              onToggleAction={(id) =>
                setCallActionDone((prev) => ({ ...prev, [id]: !prev[id] }))
              }
              onOpenAction={setCallAction}
            />
          )}
        </PushScreen>
      )}

      {/* An action from the call, over the summary. Same sheet the Actions tab
          opens, so an action reads the same wherever it is reached from. */}
      {callAction && (
        <ActionDetail
          action={callAction}
          onClose={() => setCallAction(null)}
          onSetDone={(id, done) =>
            setCallActionDone((prev) => ({ ...prev, [id]: done }))
          }
        />
      )}

      {claim?.about && (
        <ClaimSheet
          name={claim.name}
          about={claim.about}
          start={results?.start}
          onStart={() => setClaim(undefined)}
          onClose={() => setClaim(undefined)}
        />
      )}

      {!onBack && <TabBar active='profile' />}

      {/* Why the sparkles are there, said once. Without it an unreviewed profile
          is just a profile with some marks on it, and nothing has asked them to
          do anything about it. It only explains: the checking is done on the
          review screen, or row by row here. */}
      {showIntro && (
        <BottomSheet
          variant='hug'
          title={CARE_PROFILE.review.title}
          onClose={() => setShowIntro(false)}
          footer={(close) => (
            <Button variant='contained' size='large' fullWidth onClick={close}>
              {CARE_PROFILE.review.action}
            </Button>
          )}
        >
          <Typography variant='body1'>{CARE_PROFILE.review.body}</Typography>
        </BottomSheet>
      )}
    </ScreenRoot>
  );
};

export default CareProfileScreen;
